import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["input", "estimate", "dayCheckbox"];
  static values = { totalLessons: Number, examDate: String };

  connect() {
    this.update();
  }

  update() {
    const lessonsPerDay = parseInt(this.inputTarget.value) || 0;
    const studyDays = this.dayCheckboxTargets
      .filter(checkbox => checkbox.checked)
      .map(checkbox => parseInt(checkbox.value));

    if (lessonsPerDay > 0 && studyDays.length > 0) {
      let lessonsRemaining = this.totalLessonsValue;
      let currentDate = new Date();
      let calendarDays = 0;

      // Reset time to avoid edge cases near midnight
      currentDate.setHours(0, 0, 0, 0);

      // Start counting from today
      calendarDays = 1;

      while (lessonsRemaining > 0) {
        const dayOfWeek = currentDate.getDay();

        if (studyDays.includes(dayOfWeek)) {
          lessonsRemaining -= lessonsPerDay;
        }

        // If we finished today, stop counting
        if (lessonsRemaining <= 0) break;

        // Otherwise, move to next day
        calendarDays++;
        currentDate.setDate(currentDate.getDate() + 1);

        // Safety break to prevent infinite loops in case of logic errors
        if (calendarDays > 2000) break;
      }

      const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
      const dateString = currentDate.toLocaleDateString(undefined, options);

      // Exam Date Logic
      const examDate = new Date(this.examDateValue);
      let warningHtml = '';

      if (this.examDateValue && !isNaN(examDate.getTime())) {
        examDate.setHours(0, 0, 0, 0);
        const examDateString = examDate.toLocaleDateString(undefined, options);

        if (currentDate > examDate) {
          warningHtml = `
            <div class="mt-3 flex items-start gap-3 p-3 bg-amber-50 rounded-xl border border-amber-100">
              <svg class="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path></svg>
              <div class="flex flex-col">
                <span class="text-sm font-bold text-amber-800">This is after your exam!</span>
                <span class="text-xs text-amber-700">Your exam is on ${examDateString}.</span>
              </div>
            </div>
          `;
        } else {
          warningHtml = `
            <div class="mt-3 flex items-center gap-2 text-gray-500">
               <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg>
               <span class="text-xs font-medium">Exam date: ${examDateString}</span>
            </div>
          `;
        }
      }

      this.estimateTarget.classList.remove("hidden");
      this.estimateTarget.innerHTML = `
        <div class="flex items-center gap-5">
          <div class="flex-shrink-0 flex flex-col items-center justify-center w-20 h-20 bg-white text-blue-600 rounded-3xl shadow-md border-2 border-blue-50 ring-4 ring-blue-100 ring-opacity-50">
            <span class="text-3xl font-black leading-none">${calendarDays}</span>
            <span class="text-[10px] uppercase font-extrabold tracking-widest text-blue-400 mt-1">${calendarDays === 1 ? 'day' : 'days'}</span>
          </div>
          <div class="flex flex-col gap-1">
            <div class="flex items-center gap-2">
              <span class="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
              <span class="text-[11px] text-blue-600 uppercase tracking-[0.2em] font-black">Success Projection</span>
            </div>
            <span class="text-2xl font-bold text-gray-900 leading-tight">Done by ${dateString.split(',')[0]}, ${dateString.split(',').slice(1).join(',')}</span>
            <p class="text-sm text-gray-500 font-medium">at your pace of ${lessonsPerDay} lessons / day</p>
            ${warningHtml}
          </div>
        </div>
      `;
    } else if (studyDays.length === 0) {
      this.estimateTarget.classList.remove("hidden");
      this.estimateTarget.innerHTML = `
        <div class="flex items-center gap-3 p-2 text-amber-700">
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path></svg>
          <span class="text-sm font-bold">Please select at least one study day.</span>
        </div>
      `;
    } else {
      this.estimateTarget.classList.add("hidden");
      this.estimateTarget.innerHTML = "";
    }
  }
}
